import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, DoorOpen, Shield, Wrench, CheckCircle2, AlertCircle, Settings, Zap } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const Services = () => {
  useScrollAnimations();

  const lockingServices = [
    'Complex multipoint locking systems (GU, Mila, Fullex, Yale, Winkhaus, ERA, Lockmaster)',
    'Obscure and specialist lock systems others won\'t touch',
    'Euro cylinder upgrades and anti-snap security',
    'Emergency lockout and access solutions',
    'High-security lock installations and upgrades',
    'Fine adjustment and calibration for perfect operation'
  ];

  const closerServices = [
    'Commercial door closers (Dorma, Briton, GEZE, Abloy)',
    'Floor springs for pivot doors and heavy entrances',
    'Transom closers for glazed and aluminium doors',
    'Fire-rated closers (BS EN 1154 compliant)',
    'Concealed closers for clean aesthetics',
    'Hold-open devices and electromagnetic releases',
    'EN rating configuration and adjustment'
  ];

  const windowDoorServices = [
    'Tilt and turn window mechanism repairs',
    'Tilt and slide door systems',
    'uPVC and aluminium frame repairs',
    'Hinge, handle, and gearbox replacement',
    'Door and window realignment',
    'Bi-fold and sliding door servicing'
  ];

  const tiltTurnBrands = [
    'Roto NT', 'Maco Multi-Matic', 'Siegenia Aubi', 'GU (Gretsch-Unitas)', 
    'Winkhaus activPilot', 'Sobinco', 'Schüco', 'HOPPE',
    'Idealcombi', 'Rationel', 'Velfac', 'Amesbury Truth'
  ];

  const glazingServices = [
    'Safety glass to BS EN 12600 standards',
    'Toughened and laminated glass for critical locations',
    'Failed (misted/condensation) sealed units',
    'Thermal (Low-E) glass upgrades',
    'Acoustic glass for noise reduction',
    'Emergency boarding and glass replacement'
  ];

  return (
    <div>
      {/* Page Header */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Our Specialist Services
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Royal Security specialises in doors, windows, locking systems, door closers, and glazing. We diagnose correctly and repair properly—it's not just about fitting parts, it's about precision adjustment and getting it right.
          </p>
        </div>
      </section>

      {/* Repair vs Replace Section */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <div style={{
              background: 'var(--royal-white)',
              padding: '40px',
              borderRadius: '8px',
              border: '2px solid var(--royal-gold)',
              textAlign: 'center'
            }} className="scroll-animate">
              <Wrench size={48} style={{ color: 'var(--royal-gold)', marginBottom: '20px' }} />
              <h3 className="royal-heading-sm">Precision Over Speed</h3>
              <p className="royal-body">
                Anyone can fit a part. What sets us apart is the fine adjustment, calibration, and attention to detail that makes the difference between "it works" and "it works perfectly."
              </p>
            </div>
            <div style={{
              background: 'var(--royal-white)',
              padding: '40px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              textAlign: 'center'
            }} className="scroll-animate">
              <AlertCircle size={48} style={{ color: 'var(--royal-navy)', marginBottom: '20px' }} />
              <h3 className="royal-heading-sm">Specialist Knowledge</h3>
              <p className="royal-body">
                We handle the complex systems and obscure hardware that others won't touch. From rare locking mechanisms to specialist window systems—if it exists, we can repair it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Locks & Security */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            alignItems: 'start'
          }}>
            <div>
              <div style={{
                width: '72px',
                height: '72px',
                background: 'var(--royal-navy)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                color: 'var(--royal-gold)'
              }}>
                <Lock size={40} />
              </div>
              <h2 className="royal-heading-md">Locks & Security</h2>
              <p className="royal-body-lg" style={{ marginBottom: '20px' }}>
                We specialise in the complicated locking systems that other locksmiths avoid. Obscure multipoint mechanisms, discontinued hardware, high-security systems—bring us your difficult problems.
              </p>
              <p className="royal-body" style={{ marginBottom: '32px' }}>
                It's not just about installing locks—it's the fine adjustments that make all the difference. Proper alignment, correct engagement depth, smooth operation without forcing. When we finish, your lock should operate perfectly, not just adequately.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600"
                alt="Professional multipoint lock mechanism"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              />
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '20px', color: 'var(--royal-navy)' }}>
                What We Cover
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {lockingServices.map((service, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--bg-section-alt)',
                    borderRadius: '6px'
                  }}>
                    <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <div style={{
                marginTop: '32px',
                padding: '24px',
                background: 'var(--royal-navy)',
                borderRadius: '8px',
                color: 'var(--royal-white)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: 'var(--royal-gold)' }}>
                  The Difference is in the Details
                </h4>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                  Many "repaired" locks fail again within months because the underlying alignment wasn't addressed. We diagnose the root cause, make precise adjustments, and ensure long-term reliability—not quick fixes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Door Closers Section - NEW */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            alignItems: 'start'
          }}>
            <div style={{ order: 2 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '20px', color: 'var(--royal-navy)' }}>
                What We Cover
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {closerServices.map((service, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--royal-white)',
                    borderRadius: '6px'
                  }}>
                    <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <div style={{
                marginTop: '32px',
                padding: '24px',
                background: 'var(--royal-navy)',
                borderRadius: '8px',
                color: 'var(--royal-white)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: 'var(--royal-gold)' }}>
                  EN Ratings & Fire Compliance
                </h4>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                  Door closers aren't one-size-fits-all. We configure correct power sizes (EN 1-6), closing speeds, backcheck resistance, and ensure fire-rated installations meet BS EN 1154 requirements. Proper setup prevents door slam, ensures accessibility compliance, and maintains fire safety.
                </p>
              </div>
            </div>
            <div style={{ order: 1 }}>
              <div style={{
                width: '72px',
                height: '72px',
                background: 'var(--royal-navy)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                color: 'var(--royal-gold)'
              }}>
                <Settings size={40} />
              </div>
              <h2 className="royal-heading-md">Door Closers & Overhead Equipment</h2>
              <p className="royal-body-lg" style={{ marginBottom: '20px' }}>
                From commercial surface-mounted closers to concealed floor springs, we install and adjust door control equipment for all applications—offices, shops, managed buildings, and fire doors.
              </p>
              <p className="royal-body" style={{ marginBottom: '32px' }}>
                Installing a closer is straightforward. Setting it up correctly is the specialist work: opening angle limits, closing speed curves, latching velocity, backcheck from specific angles, and hold-open positions. We ensure your doors close safely, quietly, and in compliance with building regulations.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1558618047-f4b511dfce77?w=600"
                alt="Commercial door closer installation"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Windows & Doors */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            alignItems: 'start'
          }}>
            <div>
              <div style={{
                width: '72px',
                height: '72px',
                background: 'var(--royal-navy)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                color: 'var(--royal-gold)'
              }}>
                <DoorOpen size={40} />
              </div>
              <h2 className="royal-heading-md">Windows & Doors</h2>
              <p className="royal-body-lg" style={{ marginBottom: '20px' }}>
                Specialist repairs for uPVC, aluminium, and timber-composite windows and doors. From tilt and turn mechanisms to bi-fold door tracking systems.
              </p>
              <p className="royal-body" style={{ marginBottom: '32px' }}>
                We work with all major European window hardware systems. When others say "we don't do that brand," we say "bring it to us."
              </p>
              <img 
                src="https://images.unsplash.com/photo-1766802981816-732b1679a6eb?w=600"
                alt="Modern tilt and turn window system"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              />
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '20px', color: 'var(--royal-navy)' }}>
                What We Cover
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {windowDoorServices.map((service, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--bg-section-alt)',
                    borderRadius: '6px'
                  }}>
                    <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{service}</span>
                  </li>
                ))}
              </ul>
              
              {/* Brands Section */}
              <div style={{
                marginTop: '32px',
                padding: '24px',
                background: 'var(--bg-section-alt)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px', color: 'var(--royal-navy)' }}>
                  Tilt & Turn Hardware Brands We Service
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tiltTurnBrands.map((brand, index) => (
                    <span key={index} style={{
                      padding: '6px 12px',
                      background: 'var(--royal-white)',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      color: 'var(--royal-navy)',
                      border: '1px solid var(--border-color)'
                    }}>
                      {brand}
                    </span>
                  ))}
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '12px', marginBottom: 0 }}>
                  Including Scandinavian composite systems: Idealcombi, Rationel, Velfac
                </p>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '24px',
                background: 'var(--royal-navy)',
                borderRadius: '8px',
                color: 'var(--royal-white)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: 'var(--royal-gold)' }}>
                  Complex Systems Expertise
                </h4>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                  Tilt and turn, tilt and slide—these systems require genuine specialist knowledge. Incorrect adjustments create bigger problems. We understand the mechanisms, the geometry, and the precision required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Glazing Services */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            alignItems: 'start'
          }}>
            <div style={{ order: 2 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '20px', color: 'var(--royal-navy)' }}>
                What We Cover
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {glazingServices.map((service, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'var(--royal-white)',
                    borderRadius: '6px'
                  }}>
                    <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{service}</span>
                  </li>
                ))}
              </ul>
              
              {/* Regulations Box */}
              <div style={{
                marginTop: '32px',
                padding: '24px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '2px solid var(--royal-gold)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: 'var(--royal-navy)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Zap size={20} style={{ color: 'var(--royal-gold)' }} />
                  Safety Glass Regulations
                </h4>
                <p style={{ fontSize: '0.9rem', lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  UK Building Regulations require safety glass in critical locations:
                </p>
                <ul style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', paddingLeft: '20px', marginBottom: '12px' }}>
                  <li>Glass in doors up to 1500mm from floor</li>
                  <li>Glass within 300mm of door edges</li>
                  <li>Low-level glazing within 800mm of floor</li>
                </ul>
                <p style={{ fontSize: '0.85rem', color: 'var(--royal-navy)', fontWeight: '500', margin: 0 }}>
                  We ensure all replacements comply with BS EN 12600 standards.
                </p>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '24px',
                background: 'var(--royal-navy)',
                borderRadius: '8px',
                color: 'var(--royal-white)'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', color: 'var(--royal-gold)' }}>
                  Quality Materials Only
                </h4>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                  We use high-quality sealed units from reputable manufacturers. Proper spacer bars, quality seals, and argon fill where specified. Cheap units fail quickly—we focus on long-term performance.
                </p>
              </div>
            </div>
            <div style={{ order: 1 }}>
              <div style={{
                width: '72px',
                height: '72px',
                background: 'var(--royal-navy)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                color: 'var(--royal-gold)'
              }}>
                <Shield size={40} />
              </div>
              <h2 className="royal-heading-md">Glazing Services</h2>
              <p className="royal-body-lg" style={{ marginBottom: '20px' }}>
                Professional glass replacement and upgrades. We understand safety glass regulations and use quality materials that perform long-term.
              </p>
              <p className="royal-body" style={{ marginBottom: '32px' }}>
                From emergency repairs to planned upgrades—thermal performance, acoustic reduction, or safety compliance—we provide honest advice on what you actually need.
              </p>
              <img 
                src="https://images.unsplash.com/photo-1723400830780-64e3d550705b?w=600"
                alt="Professional double glazing installation"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'var(--royal-white)'
          }}>
            Need a Specialist Assessment?
          </h2>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Whether it's a complex locking system, a temperamental door closer, or specialist window hardware—we have the knowledge to help.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Request a Quote
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
